import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock3, ShieldCheck, XCircle } from "lucide-react";
import { useState } from "react";
import { useParams } from "wouter";

import { api } from "@/api";
import {
  Button,
  ErrorState,
  EmptyState,
  Panel,
  SectionHeading,
  Skeleton,
} from "@/components/ui";
import { trackTitle } from "@/features/racing/venue";
import { verifyRaceById } from "../../../shell/verify.js";

type VerificationResult = {
  card: {
    raceId: string;
    track: string;
    trackName?: string;
    distanceM: number;
    nonce: string;
  };
  checks: Array<{
    label: string;
    ok: boolean | null;
    detail: string;
  }>;
};

export default function VerifyPage() {
  const params = useParams<{ raceId?: string }>();
  const [raceId, setRaceId] = useState(params.raceId ?? "");
  const [activeId, setActiveId] = useState(params.raceId ?? "");
  const results = useQuery({
    queryKey: ["races", "results", 10],
    queryFn: () => api.results(10),
  });
  const verification = useQuery({
    queryKey: ["verification", activeId],
    queryFn: () => verifyRaceById(activeId) as Promise<VerificationResult>,
    enabled: Boolean(activeId),
    retry: false,
  });

  return (
    <div className="page page--verify">
      <SectionHeading
        icon={<ShieldCheck aria-hidden />}
        title="Fairness Verifier"
      />
      <Panel className="verify-intro">
        <h1>Never trust. Recompute.</h1>
        <p>
          The signed racecard, committed composition, seed chain, finish order
          and telemetry all re-derive in this browser from public facts.
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setActiveId(raceId.trim());
          }}
        >
          <input
            aria-label="Race UUID"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => setRaceId(event.target.value)}
            placeholder="Race UUID"
            value={raceId}
          />
          <Button disabled={!raceId.trim()} type="submit">
            Verify race
          </Button>
        </form>
      </Panel>

      {verification.isPending && activeId ? (
        <Skeleton rows={7} variant="table" />
      ) : verification.error ? (
        <ErrorState
          error={verification.error}
          retry={() => void verification.refetch()}
        />
      ) : verification.data ? (
        <Panel className="verification-results">
          <header>
            <strong>
              {trackTitle(verification.data.card)} ·{" "}
              {verification.data.card.distanceM}m
            </strong>
            <span>{verification.data.card.nonce}</span>
          </header>
          {verification.data.checks.map((check) => (
            <article key={check.label}>
              {check.ok === null ? (
                <Clock3 aria-hidden />
              ) : check.ok ? (
                <CheckCircle2 aria-hidden />
              ) : (
                <XCircle aria-hidden />
              )}
              <div>
                <strong>{check.label}</strong>
                <span>{check.detail}</span>
              </div>
            </article>
          ))}
        </Panel>
      ) : null}

      <SectionHeading title="Recent results" />
      <Panel className="verify-recent">
        {results.error ? (
          <ErrorState
            error={results.error}
            retry={() => void results.refetch()}
          />
        ) : null}
        {results.isPending ? (
          <Skeleton rows={6} variant="table" />
        ) : results.data?.races.length === 0 ? (
          <EmptyState
            title="No official races yet"
            detail="Completed races will appear here for verification."
          />
        ) : (
          results.data?.races.map((race) => (
            <button
              key={race.raceId}
              onClick={() => {
                setRaceId(race.raceId);
                setActiveId(race.raceId);
              }}
              type="button"
            >
              <span>
                <strong>{trackTitle(race)}</strong>
                <small>{race.distanceM}m</small>
              </span>
              <code>{race.raceId.slice(0, 12)}…</code>
            </button>
          ))
        )}
      </Panel>
    </div>
  );
}
